import { useState, useEffect } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { getCreditAddingPlan, createCreditOrder, verifyCreditPayment, getUserCredits } from "../apis/subscription";
import { loadScript } from "../utils/razorpay";

const CreditPurchaseModal = ({ isOpen, onClose, onSuccess }) => {
  const [addingPlan, setAddingPlan] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userCredits, setUserCredits] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadAddingPlan();
      loadUserCredits();
    }
  }, [isOpen]);

  const loadAddingPlan = async () => {
    try {
      setIsLoading(true);
      const response = await getCreditAddingPlan();
      if (response.data.success) {
        setAddingPlan(response.data.data);
      } else {
        toast.error(response.data.error || "Failed to load credit plan");
      }
    } catch (error) {
      console.error("Error loading adding plan:", error);
      toast.error("Failed to load credit plan");
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserCredits = async () => {
    try {
      const response = await getUserCredits();
      if (response.data.success) {
        setUserCredits(response.data.data);
      }
    } catch (error) {
      console.error("Error loading user credits:", error);
    }
  };

  const handlePurchase = async () => {
    if (!addingPlan || creditsAmount <= 0) {
      toast.error("Please enter credits amount");
      return;
    }

    setIsProcessing(true);

    try {
      await loadScript();

      const orderResponse = await createCreditOrder(addingPlan.id, creditsAmount);
      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.error || "Failed to create order");
      }

      const { order_id, amount, key_id } = orderResponse.data.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "Blutec PING",
        description: `Purchase ${creditsAmount} AI Credits`,
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await verifyCreditPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResponse.data.success) {
              toast.success(`Successfully purchased ${creditsAmount} credits!`);
              await loadUserCredits();
              if (onSuccess) {
                onSuccess(verifyResponse.data.data);
              }
              onClose();
            } else {
              throw new Error(verifyResponse.data.error || "Payment verification failed");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error(error.response?.data?.error || "Payment verification failed");
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: JSON.parse(localStorage.getItem("user") || "{}").name || "",
          email: JSON.parse(localStorage.getItem("user") || "{}").email || "",
        },
        theme: {
          color: "#048AAD",
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error(error.response?.data?.error || "Failed to initiate payment");
      setIsProcessing(false);
    }
  };

  const calculateTotal = () => {
    if (!addingPlan || creditsAmount <= 0) return 0;
    return (addingPlan.per_credit_price * creditsAmount).toFixed(2);
  };

  const calculateReplies = () => {
    if (!addingPlan || creditsAmount <= 0) return 0;
    return creditsAmount * addingPlan.per_credit_ai_replies;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-[#048AAD] to-[#01CC54] p-6 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Purchase AI Credits</h2>
                  {userCredits && (
                    <p className="text-white/90 text-sm mt-1">
                      Current: {userCredits.totalAvailableReplies} AI replies available
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#048AAD]" />
              </div>
            ) : !addingPlan ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No credit plan available. Please contact support.</p>
              </div>
            ) : (
              <>
                {/* Plan Info (Read-only) */}
                <div className="mb-6 p-4 bg-gradient-to-r from-[#048AAD]/10 to-[#01CC54]/10 rounded-xl border-2 border-[#048AAD]/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-600">Plan</div>
                      <div className="text-lg font-semibold text-gray-900">{addingPlan.plan_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Price per credit</div>
                      <div className="text-lg font-bold text-[#048AAD]">₹{addingPlan.per_credit_price}</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#048AAD]/20">
                    <div className="text-sm text-gray-600">
                      1 credit = {addingPlan.per_credit_ai_replies} AI replies
                    </div>
                  </div>
                </div>

                {/* Credits Amount */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Credits to Add
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={creditsAmount}
                    onChange={(e) => setCreditsAmount(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#048AAD] focus:outline-none text-lg font-semibold"
                    placeholder="Enter credits amount"
                  />
                  {creditsAmount > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">{creditsAmount}</span> credits ={" "}
                        <span className="font-bold text-[#048AAD]">{calculateReplies()}</span> AI replies
                      </p>
                    </div>
                  )}
                </div>

                {/* Total */}
                {creditsAmount > 0 && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-[#048AAD]/10 to-[#01CC54]/10 rounded-xl border-2 border-[#048AAD]/20">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-gray-700">Total Amount:</span>
                      <span className="text-2xl font-bold text-[#048AAD]">₹{calculateTotal()}</span>
                    </div>
                  </div>
                )}

                {/* Purchase Button */}
                <button
                  onClick={handlePurchase}
                  disabled={!addingPlan || creditsAmount <= 0 || isProcessing}
                  className="w-full py-4 bg-gradient-to-r from-[#048AAD] to-[#01CC54] text-white font-bold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Purchase Credits
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreditPurchaseModal;

